import { useEffect, type ReactNode } from 'react'
import styles from './AuthCard.module.css'

export interface AuthCardProps {
  /** Browser tab title. `index.html`'s own static title is "Operations console" — the OPERATOR surface's name, which is what a customer's tab said on every one of these screens. */
  documentTitle: string
  title: string
  /** One line saying what this screen is for, before the first field. */
  lede?: ReactNode
  children: ReactNode
  footnote?: ReactNode
}

/**
 * `DES-001`. The frame for a screen a person meets before they have a
 * session — first built for `/customer/login` and
 * `/customer/accept-invite/:inviteId`, then reused for the operator's own
 * `/login` and `/reset-password` once the audit found the exact same gap on
 * both sides of this one codebase: a raw, unstyled form with no product
 * identity, on an empty ground, while every authenticated screen behind it
 * uses a complete design system.
 *
 * `/login`'s own comment used to say styling it was *"Phase 3b's work, not
 * this one's."* Phase 3b shipped — `tokens.css`, every `ui/` primitive,
 * `KitchenSinkPage` demonstrating all of it — and nothing came back to this
 * screen, which is the first thing a Pruvato operator sees every single day.
 *
 * Card rather than the console's own hairline-rule sectioning on purpose: the
 * instrument register (UI spec §8, Register B) is for a surface dense with
 * data, and a pre-session screen is a single short form on an otherwise empty
 * viewport. The tokens are the console's throughout — this is the same
 * palette, not a third register — and "Pruvato" is the honest mark for both:
 * the operator console is a Pruvato-staff tool, not a different product.
 */
export function AuthCard({ documentTitle, title, lede, children, footnote }: AuthCardProps) {
  useEffect(() => {
    document.title = documentTitle
  }, [documentTitle])

  return (
    <main className={styles['page']}>
      <div className={styles['card']}>
        <p className={styles['mark']}>Pruvato</p>
        <h1 className={styles['title']}>{title}</h1>
        {lede !== undefined && <p className={styles['lede']}>{lede}</p>}
        {children}
        {footnote !== undefined && <p className={styles['footnote']}>{footnote}</p>}
      </div>
    </main>
  )
}

export { styles as authCardStyles }
